// Core lib dependencies.
import React from 'react';
import PropTypes from 'prop-types';

// Component dependencies.
import { Button } from '../../../../common/components/buttons.jsx';

// Misc dependencies.
import { getLabel } from '../../../../common/utils/string.js';

// Config.
import Config from '../../config/config.js';
import Enums from '../../config/enums.js';

/**
 * <pre>
 * UgcWall > Toolbars > Sorting Toolbar React component.
 *
 * Summary of responsibilities:
 *
 *  - outputs a toolbar to allow resultset to be sorted by various criteria (eg. date, username, votes).
 * </pre>
 *
 * @memberof module:UgcWall.Components.Elements.Toolbars
 * @namespace
 *
 * @return {module:UgcWall.Components.Elements.Toolbars.SortingToolbar}
 */
class SortingToolbar extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);

    // Any registered select/combo sorts.
    this.selectSorts = [];

    // Define a list of sorting to output.
    this.sortingConfig = this.buildSortingConfig(props.contentServicesVersion, props.displayMode, props.sortBy);

    // Bind methods.
    this.handleButtonClicked = this.handleButtonClicked.bind(this);
    this.handleSelectChanged = this.handleSelectChanged.bind(this);
    this.applySorting = this.applySorting.bind(this);
  }

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Content Services related.
  buildSortingConfig(contentServicesVersion, displayMode, sortBy) {
    // Define config for different Content Services versions.
    const _contentServiceConfig = {
      v1: {
        sorts: [
          {
            name:  'Published',
            value: 'publishDate',
            label: getLabel('ugc.sorting.date.published'),
          },
          { name: 'name', value: 'name', label: getLabel('ugc.sorting.name') },
          { name: 'random', value: 'random', label: getLabel('ugc.sorting.random') },
        ],
      },

      v2: {
        sorts: [
          { name: 'created', value: 'createDate', label: getLabel('ugc.sorting.date.date') },
          { name: 'name', value: 'name', label: getLabel('ugc.sorting.name') },
          { name: 'random', value: 'random', label: getLabel('ugc.sorting.random') },
        ],
      },
    };

    const _sortConfig = _contentServiceConfig[contentServicesVersion].sorts;

    // If voting enabled.
    if (this.props.votingEnabled) {
      _sortConfig.push({ name: 'votes', value: 'likes', label: getLabel('ugc.sorting.votes') });
    }

    // if custom ordered, do not show a default selected value
    if (displayMode === Enums.SORT_DISPLAY_MODE_SELECT && (sortBy === 'custom' || sortBy === 'publishDate')) {
      _sortConfig.unshift({ name: '', value: '', label: 'Sort by...' });
    }

    return _sortConfig;
  }

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Lifecycle methods.

  /**
   * Called when component mounts for the first time.
   * Initializes Selectize combo box if in drop down render mode.
   */
  componentDidMount() {
    // Init any select/combo filters.
    this.selectSorts.map((sort, index) => {
      const sortRef = sort.ref;

      // Init select.
      if (this.refs[sortRef]) {
        const selectize = $(this.refs[sortRef]).selectize({
          wrapperClass:         'xComboWrapper',
          inputClass:           'xComboInput',
          dropdownClass:        'xComboOptions',
          dropdownContentClass: 'xComboOption',
          selectOnTab:          true,
        })[0].selectize;

        // Bind the current filter type to the handler.
        const handler = this.handleSelectChanged;

        // Can't use React's event bindings for this :(
        selectize.on('change', handler);
      }
    });
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:UgcWall.Components.Elements.Toolbars.SortingToolbar}
   */
  render() {
    this.selectSorts = [];

    let _controls;
    let _label;
    let _hasLabels = false;

    // Output filter using correct display mode.
    switch (this.props.displayMode) {
      // Output as select/combo input.
      case Enums.SORT_DISPLAY_MODE_SELECT:
        _label = this.renderLabel('ugc.sorting.label');
        if (_label) {
          _hasLabels = true;
        }
        _controls = this.renderAsSelect(this.sortingConfig);

        break;

      // Output as buttons.
      case Enums.SORT_DISPLAY_MODE_BUTTON:
      default:
        _controls = this.renderAsButtons(this.sortingConfig);

        break;
    }

    // Define additional classes for container.
    let _classes = 'xActionContainer xActionSortContainer';
    _classes += _hasLabels ? ' xHasLabels' : '';

    return (
      <div className={_classes}>
        {_label}
        {_controls}
      </div>
    );
  }

  /**
   * Render controls as a select element.
   *
   * @param  {Array} sortingConfig Array containg a list of sort objects to output.
   * @return {ReactElement} The sorting select elements.
   */
  renderAsSelect(sortingConfig) {
    let _selectControls;

    // Define ref.
    const _ref = 'sortSelect';

    // Loop over list of sorting and generate options markup.
    const _selectOptions = sortingConfig.map((sort, index) => {
      return (
        <option key={index} value={sort.value}>
          {sort.label}
        </option>
      );
    });

    _selectControls = <select ref={_ref}>{_selectOptions}</select>;

    // Store reference to this select so that we can apply selectize to it on component mount.
    this.selectSorts.push({ ref: _ref });

    return _selectControls;
  }

  /**
   * Render controls as a series of buttons.
   *
   * @param  {Array} sortingConfig Array containg a list of sort objects to output.
   * @return {ReactElement} The sorting button elements.
   */
  renderAsButtons(sortingConfig) {
    let _controls;

    const _buttons = sortingConfig.map((sort, index) => {
      const active = this.props.sortBy === sort.value ? 'active' : '';
      const sortDir = active ? this.props.sortDir : 'desc';

      // Bind the current sorting config object to the click handler.
      const handler = this.handleButtonClicked.bind(this, sort);

      return (
        <li key={index} className="xAction xActionSort">
          <Button onClick={handler} className={'xButton sortEntries ' + sort.name + ' ' + sortDir + ' ' + active}>
            {sort.label}
          </Button>
        </li>
      );
    });

    _controls = <ul className="xToolbar xButtonToolbar xActionSortButtons">{_buttons}</ul>;

    return _controls;
  }

  /**
   * Render a label element, given a label path.
   *
   * @param  {String} labelPath Path to the required localisation label.
   * @return {ReactElement} The label element.
   */
  renderLabel(labelPath) {
    let _label;
    const _labelValue = getLabel(labelPath);

    if (_labelValue) {
      _label = <label className="xFieldLabel">{_labelValue}:</label>;
    }

    return _label;
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Event handler methods */

  /**
   * Handler called when a button is clicked.
   *
   * @param {Object} sortDef The sort definition object of the clicked button.
   */
  handleButtonClicked(sortDef) {
    const sortBy = sortDef.value;
    const sortDir = this.props.sortDir === 'desc' ? 'asc' : 'desc';

    this.applySorting(sortBy, sortDir);
  }

  /**
   * Handler called when a select option changes.
   *
   * @param {Object} sortBy The sort definition object of the selected option.
   */
  handleSelectChanged(sortBy) {
    const sortDir = this.props.sortDir;

    this.applySorting(sortBy, sortDir);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Action dispatcher proxy methods */

  /**
   * Attempt to call the injected sort handler method.
   *
   * @param {String} sortBy The sort column.
   * @param {String} sortDir The sort direction.
   */
  applySorting(sortBy, sortDir) {
    if (this.props.clickHandlers.sort) {
      this.props.clickHandlers.sort(sortBy, sortDir);
    }
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {String} sortBy The sort column.
 * @property {String} sortDir The sort order.
 * @property {Boolean} votingEnabled Boolean indicating if voting is enabled on the composing component.
 * @property {Object} clickHandlers Object containing event handlers for various events.
 * @property {String} displayMode The rendering mode (eg select menu|buttons)
 */
SortingToolbar.propTypes = {
  sortBy:        PropTypes.string,
  sortDir:       PropTypes.string,
  votingEnabled: PropTypes.bool,
  clickHandlers: PropTypes.object,
  displayMode:   PropTypes.string,
};

/**
 * React default props.
 *
 * @type {Object}
 */
SortingToolbar.defaultProps = {
  displayMode: Enums.SORT_DISPLAY_MODE_BUTTON,
};

export default SortingToolbar;
